import type { AppLocale } from "@metaadsmanager/tk-master-contracts";
import { IntlProvider } from "use-intl";
import {
  useCallback,
  useLayoutEffect,
  useMemo,
  useState,
  type ReactNode,
} from "react";

import {
  getBrowserTimeZone,
  writeLocalePreference,
} from "../../i18n/config";
import {
  notifyLocaleChanged,
  useClientLocale,
} from "../../i18n/use-client-locale-snapshot";
import { createAppI18n, I18nContext } from "../../i18n/context";
import { messages } from "../../i18n/messages";

function updateDocumentMetadata(locale: AppLocale): void {
  const localeMessages = messages[locale];
  document.documentElement.lang = locale;
  document.documentElement.setAttribute("data-locale", locale);
  document.title = localeMessages.meta.title;
  let description = document.querySelector<HTMLMetaElement>(
    'meta[name="description"]',
  );
  if (!description) {
    description = document.createElement("meta");
    description.name = "description";
    document.head.append(description);
  }
  description.content = localeMessages.meta.description;
}

/**
 * 客户端语言 Provider。locale 来自 useSyncExternalStore(localStorage)。
 */
export function LocaleProvider({ children }: { children: ReactNode }) {
  const locale = useClientLocale();
  const [timeZone] = useState(getBrowserTimeZone);

  useLayoutEffect(() => {
    updateDocumentMetadata(locale);
  }, [locale]);

  const setLocale = useCallback((nextLocale: AppLocale) => {
    // 先写偏好并发布 DOM 快照，再通知订阅者；否则 getLocaleSnapshot 仍读到旧 data-locale，首击不重渲染。
    writeLocalePreference(nextLocale);
    updateDocumentMetadata(nextLocale);
    notifyLocaleChanged();
  }, []);
  const value = useMemo(
    () => createAppI18n(locale, setLocale, timeZone),
    [locale, setLocale, timeZone],
  );

  return (
    <IntlProvider locale={locale} messages={messages[locale]} timeZone={timeZone}>
      <I18nContext.Provider value={value}>{children}</I18nContext.Provider>
    </IntlProvider>
  );
}
